import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, startOfMonth, startOfQuarter, startOfYear, endOfYear, subYears } from 'date-fns'

export type PeriodOption = 'questo_mese' | 'trimestre' | 'ytd' | 'anno_scorso' | 'custom'

export const PERIOD_LABELS: Record<PeriodOption, string> = {
  questo_mese: 'Questo mese',
  trimestre:   'Trimestre',
  ytd:         'Anno in corso',
  anno_scorso: 'Anno scorso',
  custom:      'Personalizzato',
}

export interface GlobalFilters {
  period?: PeriodOption
  date_from?: string   // ISO date (solo per period='custom')
  date_to?: string     // ISO date (solo per period='custom')
  customer?: string
  service_line?: string
}

interface GlobalFiltersContextType {
  filters: GlobalFilters
  setFilter: <K extends keyof GlobalFilters>(key: K, value: GlobalFilters[K] | undefined) => void
  setFilters: (f: GlobalFilters) => void
  clearFilters: () => void
  hasActiveFilters: boolean
  /** Date range da passare all'API (calcolato da period o da date_from/date_to custom) */
  apiDateFrom?: string
  apiDateTo?: string
}

const GlobalFiltersContext = createContext<GlobalFiltersContextType | null>(null)

const STORAGE_KEY = 'km_global_filters'

function readFromStorage(): GlobalFilters {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as GlobalFilters) : {}
  } catch {
    return {}
  }
}

function readFromUrl(searchParams: URLSearchParams): GlobalFilters {
  const f: GlobalFilters = {}
  const period = searchParams.get('period') as PeriodOption | null
  if (period && period in PERIOD_LABELS) f.period = period
  const df = searchParams.get('date_from')
  if (df) f.date_from = df
  const dt = searchParams.get('date_to')
  if (dt) f.date_to = dt
  const customer = searchParams.get('customer')
  if (customer) f.customer = customer
  const sl = searchParams.get('service_line')
  if (sl) f.service_line = sl
  return f
}

function computeApiDateRange(filters: GlobalFilters): { apiDateFrom?: string; apiDateTo?: string } {
  const today = new Date()

  if (filters.period === 'custom') {
    return { apiDateFrom: filters.date_from, apiDateTo: filters.date_to }
  }
  if (!filters.period) return {}

  const todayStr = format(today, 'yyyy-MM-dd')

  switch (filters.period) {
    case 'questo_mese':
      return {
        apiDateFrom: format(startOfMonth(today), 'yyyy-MM-dd'),
        apiDateTo: todayStr,
      }
    case 'trimestre':
      return {
        apiDateFrom: format(startOfQuarter(today), 'yyyy-MM-dd'),
        apiDateTo: todayStr,
      }
    case 'ytd':
      return {
        apiDateFrom: format(startOfYear(today), 'yyyy-MM-dd'),
        apiDateTo: todayStr,
      }
    case 'anno_scorso': {
      const prevYear = subYears(today, 1)
      return {
        apiDateFrom: format(startOfYear(prevYear), 'yyyy-MM-dd'),
        apiDateTo: format(endOfYear(prevYear), 'yyyy-MM-dd'),
      }
    }
    default:
      return {}
  }
}

export function GlobalFiltersProvider({ children }: { children: React.ReactNode }) {
  const [searchParams, setSearchParams] = useSearchParams()

  const [filters, setFiltersState] = useState<GlobalFilters>(() => {
    const fromUrl = readFromUrl(searchParams)
    if (Object.keys(fromUrl).length > 0) return fromUrl
    return readFromStorage()
  })

  const applyFilters = useCallback((newFilters: GlobalFilters) => {
    setFiltersState(newFilters)
    // Persiste in sessionStorage per sopravvivere alla navigazione
    try {
      if (Object.keys(newFilters).length === 0) {
        sessionStorage.removeItem(STORAGE_KEY)
      } else {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newFilters))
      }
    } catch { /* storage non disponibile */ }
    // Sincronizza URL params (per condivisibilità)
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        ;['period', 'date_from', 'date_to', 'customer', 'service_line'].forEach(k => next.delete(k))
        if (newFilters.period) next.set('period', newFilters.period)
        if (newFilters.date_from) next.set('date_from', newFilters.date_from)
        if (newFilters.date_to) next.set('date_to', newFilters.date_to)
        if (newFilters.customer) next.set('customer', newFilters.customer)
        if (newFilters.service_line) next.set('service_line', newFilters.service_line)
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  const setFilter = useCallback(
    <K extends keyof GlobalFilters>(key: K, value: GlobalFilters[K] | undefined) => {
      const updated = { ...filters }
      if (value === undefined || value === '') {
        delete updated[key]
      } else {
        updated[key] = value
      }
      applyFilters(updated)
    },
    [filters, applyFilters]
  )

  const setFilters = useCallback(
    (f: GlobalFilters) => applyFilters(f),
    [applyFilters]
  )

  const clearFilters = useCallback(() => applyFilters({}), [applyFilters])

  const hasActiveFilters = !!(filters.period || filters.customer || filters.service_line)

  const { apiDateFrom, apiDateTo } = useMemo(
    () => computeApiDateRange(filters),
    [filters]
  )

  return (
    <GlobalFiltersContext.Provider value={{
      filters,
      setFilter,
      setFilters,
      clearFilters,
      hasActiveFilters,
      apiDateFrom,
      apiDateTo,
    }}>
      {children}
    </GlobalFiltersContext.Provider>
  )
}

export function useGlobalFilters() {
  const ctx = useContext(GlobalFiltersContext)
  if (!ctx) throw new Error('useGlobalFilters must be used within GlobalFiltersProvider')
  return ctx
}
